import axios from 'axios'
import { getApiGatewayUrl } from '@/config/runtime'

export type AccountPurpose = 'enrollment' | 'recovery'
export interface AccountBootstrap {
  command_id: string
  bootstrap_secret: string
}
export interface AccountProgress {
  command_id: string
  status: 'awaiting_email' | 'email_verified' | 'mfa_setup' | 'complete' | 'superseded'
  terms_version: string
  user_id?: string
}
export interface AccountMfaSetup {
  secret: string
  qr_code_url: string
  backup_codes: string[]
}
export interface AccountStart extends AccountBootstrap {
  email: string
  previous_command_id?: string
}
export interface AccountSetup extends AccountBootstrap {
  full_name: string
  password: string
}
export interface AccountFinish extends AccountBootstrap {
  mfa_code: string
  accepted: true
  terms_version: string
}
export interface AccountPassword extends AccountBootstrap {
  password: string
  mfa_code: string
}

// This public purpose channel has no shared auth, refresh, retry or mock adapter.
export const accountBootstrapClient = axios.create({
  baseURL: getApiGatewayUrl(),
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
  withCredentials: false,
})

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
export function newAccountBootstrap(): AccountBootstrap {
  const bytes = crypto.getRandomValues(new Uint8Array(32))
  return {
    command_id: crypto.randomUUID(),
    bootstrap_secret: Array.from(bytes, value => value.toString(16).padStart(2, '0')).join(''),
  }
}
export const validAccountReference = (value: string) => uuid.test(value)

export class AccountCommandError extends Error {
  constructor(
    message: string,
    readonly uncertain: boolean,
    readonly status?: number
  ) {
    super(message)
  }
}

function failure(error: unknown): AccountCommandError {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined
  const message =
    status === 410
      ? 'This request has expired. Start a fresh email-proof request.'
      : status === 429
        ? 'Please wait before trying again. Proof attempts or email requests are temporarily limited.'
        : status === 401 || status === 403
          ? 'The proof could not be accepted. Check the current email, password and authenticator code.'
          : status === 404
            ? 'This request is unavailable, or account enrollment or recovery is currently disabled.'
            : status === 422 || status === 413
              ? 'Check the account fields, password requirements and proof format.'
              : status === 409
                ? 'The request state or original fields differ. Check its status before continuing.'
                : 'The result could not be confirmed. Keep this page open and check the request status.'
  return new AccountCommandError(message, !status || status >= 500, status)
}

function object(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}
function progress(value: unknown, command: string): value is AccountProgress {
  return (
    object(value) &&
    Object.keys(value).every(key =>
      ['command_id', 'status', 'terms_version', 'user_id'].includes(key)
    ) &&
    value.command_id === command &&
    ['awaiting_email', 'email_verified', 'mfa_setup', 'complete', 'superseded'].includes(
      String(value.status)
    ) &&
    typeof value.terms_version === 'string' &&
    /^[a-zA-Z0-9_.-]{1,64}$/.test(value.terms_version) &&
    (value.status === 'complete'
      ? typeof value.user_id === 'string' && uuid.test(value.user_id)
      : value.user_id === undefined)
  )
}
function mfaSetup(value: unknown): value is AccountMfaSetup {
  return (
    object(value) &&
    Object.keys(value).length === 3 &&
    typeof value.secret === 'string' &&
    /^[A-Z2-7]{32}$/.test(value.secret) &&
    typeof value.qr_code_url === 'string' &&
    value.qr_code_url.startsWith('otpauth://totp/') &&
    Array.isArray(value.backup_codes) &&
    value.backup_codes.length === 10 &&
    value.backup_codes.every(
      code => typeof code === 'string' && /^[A-F0-9]{4}-[A-F0-9]{4}$/.test(code)
    )
  )
}

async function post<T>(
  path: string,
  body: AccountBootstrap,
  valid: (value: unknown) => value is T
): Promise<T> {
  let value: unknown
  try {
    value = (
      await accountBootstrapClient.post(path, body, { headers: { Authorization: undefined } })
    ).data
  } catch (error) {
    throw failure(error)
  }
  if (!valid(value))
    throw new AccountCommandError(
      'The server result could not be confirmed. Check the request status.',
      true
    )
  return value
}
function step(purpose: AccountPurpose, operation: string, body: AccountBootstrap) {
  const expected =
    operation === 'start' || operation === 'resend'
      ? 'awaiting_email'
      : operation === 'verify-email'
        ? 'email_verified'
        : operation === 'finish' || operation === 'password'
          ? 'complete'
          : undefined
  return post(
    `/auth/client/${purpose}/${operation}`,
    body,
    (value): value is AccountProgress =>
      progress(value, body.command_id) && (!expected || value.status === expected)
  )
}
export const accountEnrollment = {
  start: (purpose: AccountPurpose, body: AccountStart) => step(purpose, 'start', body),
  status: (purpose: AccountPurpose, body: AccountBootstrap) => step(purpose, 'status', body),
  resend: (purpose: AccountPurpose, body: AccountBootstrap) => step(purpose, 'resend', body),
  verifyEmail: (purpose: AccountPurpose, body: AccountBootstrap & { email_proof: string }) =>
    step(purpose, 'verify-email', body),
  setup: (body: AccountSetup) => post('/auth/client/enrollment/mfa-setup', body, mfaSetup),
  finish: (body: AccountFinish) => step('enrollment', 'finish', body),
  password: (body: AccountPassword) => step('recovery', 'password', body),
}
